// NOT-01 — customer-notification dead-letter queue + operator reconciliation.
//
// Reads (`/notifications/reconciliation`) are open to any console role, same
// as the dispatch board; the one mutation (`requeue`) is owner/admin only —
// the backend (app/api/console_portal/notifications.py) enforces this with
// `require_console_role("owner", "admin")`, this page's role check is UX
// only (hiding a button that would 403 rather than pretending it doesn't
// exist).
import { useCallback, useEffect, useState } from "react";
import { RotateCcw, TriangleAlert } from "lucide-react";

import { ApiError, apiGet, apiPost } from "../api";
import { useAuth } from "../auth";
import { projectionAsOf } from "../money";
import type { DlqRow, DlqStatus, ReconciliationData } from "../types";

type StatusFilter = "all" | DlqStatus;

const TABS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "dead_letter", label: "Dead letter" },
  { value: "suppressed", label: "Suppressed" },
  { value: "unknown", label: "Unknown outcome" },
  { value: "provider_failed", label: "Provider failed" },
];

function statusLabel(status: string): string {
  return status.replace(/_/g, " ");
}

function DlqRowView({
  row,
  canRequeue,
  onRequeue,
}: {
  row: DlqRow;
  canRequeue: boolean;
  onRequeue: (id: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const requeue = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      await onRequeue(row.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Requeue failed");
    } finally {
      setBusy(false);
    }
  }, [onRequeue, row.id]);

  return (
    <div className="dlq-row">
      <span className={`dlq-status dlq-status-${row.status}`}>{statusLabel(row.status)}</span>
      <div className="dlq-main">
        <span className="dlq-event">
          {row.event} · <span className="dlq-channel">{row.channel}</span>
        </span>
        <span className="dlq-sub">
          {row.recipient || "unknown recipient"} · {row.attempts} attempt{row.attempts === 1 ? "" : "s"}
          {row.provider ? ` · ${row.provider}` : ""}
        </span>
        {row.last_error && <span className="dlq-error">{row.last_error}</span>}
      </div>
      <div className="dlq-meta">
        <span className="dlq-time">{new Date(row.created_at).toLocaleString()}</span>
        {row.next_attempt_at && (
          <span className="dlq-time-sub">next {new Date(row.next_attempt_at).toLocaleString()}</span>
        )}
        {row.sent_at && <span className="dlq-time-sub">sent {new Date(row.sent_at).toLocaleString()}</span>}
      </div>
      {row.requeue_available && canRequeue && (
        <button className="courier-btn dlq-requeue" onClick={() => void requeue()} disabled={busy}>
          <RotateCcw size={12} /> {busy ? "Requeuing…" : "Requeue"}
        </button>
      )}
      {err && <span className="dlq-row-err">{err}</span>}
    </div>
  );
}

export default function Notifications() {
  const { user } = useAuth();
  const canRequeue = user?.role === "owner" || user?.role === "admin";
  const [status, setStatus] = useState<StatusFilter>("all");
  const [data, setData] = useState<ReconciliationData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (s: StatusFilter) => {
    try {
      const qs = s === "all" ? "" : `?status=${s}`;
      setData(await apiGet<ReconciliationData>(`/notifications/reconciliation${qs}`));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load the notification DLQ");
    }
  }, []);

  useEffect(() => {
    void load(status);
  }, [load, status]);
  useEffect(() => {
    const t = setInterval(() => void load(status), 20000);
    return () => clearInterval(t);
  }, [load, status]);

  const requeue = useCallback(
    async (id: string) => {
      await apiPost(`/notifications/${id}/requeue`);
      await load(status);
    },
    [load, status],
  );

  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Notification DLQ</h1>
      </div>
      {err && (
        <div className="banner-err">
          {err}
          {data ? " — showing the last successful read, which may be stale." : " — DLQ data is unavailable."}
        </div>
      )}
      {data && (
        <p className="cap-note">
          {projectionAsOf(data.as_of)}
          {data.truncated ? " More rows are omitted; counts below still cover every reconciliation state." : ""}
        </p>
      )}

      <div className="kpis dlq-kpis">
        <div className="kpi kpi-risk">
          <div className="kpi-icon">
            <TriangleAlert size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">{data?.counts.dead_letter ?? "—"}</div>
            <div className="kpi-label">Dead letter (requeue-eligible)</div>
          </div>
        </div>
        <div className="kpi kpi-attention">
          <div className="kpi-icon">
            <TriangleAlert size={18} />
          </div>
          <div className="kpi-body">
            <div className="kpi-value">{data?.counts.suppressed ?? "—"}</div>
            <div className="kpi-label">Suppressed (not a send failure)</div>
          </div>
        </div>
        <div className="kpi kpi-risk">
          <div className="kpi-body">
            <div className="kpi-value">{data?.counts.unknown ?? "—"}</div>
            <div className="kpi-label">Unknown (never auto-retry)</div>
          </div>
        </div>
        <div className="kpi kpi-attention">
          <div className="kpi-body">
            <div className="kpi-value">{data?.counts.provider_failed ?? "—"}</div>
            <div className="kpi-label">Provider failed</div>
          </div>
        </div>
      </div>
      {!canRequeue && (
        <p className="cap-note">Your role ({user?.role}) can view this queue but not requeue — that needs owner or admin.</p>
      )}

      <div className="dlq-tabs">
        {TABS.map((t) => (
          <button
            key={t.value}
            className={`dlq-tab ${status === t.value ? "active" : ""}`}
            onClick={() => setStatus(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="lane dlq-lane">
        <div className="lane-body">
          {!data ? (
            <p className="lane-empty">{err ? "DLQ data unavailable" : "Loading the notification DLQ…"}</p>
          ) : data.items.length === 0 ? (
            <p className="lane-empty">No {status === "all" ? "reconciliation" : statusLabel(status)} rows.</p>
          ) : (
            data.items.map((row) => (
              <DlqRowView key={row.id} row={row} canRequeue={canRequeue} onRequeue={requeue} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}
